/**
 * OperationsPanel.jsx — Rê Leve
 * Painel 2 da Janela do Operador. Acompanhamento de tarefas dos próximos dias:
 * lista de compras agregada (amanhã / esta semana / próxima semana),
 * checklist de cozinha, agenda de entregas, alertas de estoque.
 */

import { useMemo, useState } from 'react';
import {
  getPurchaseList,
  getHorizons,
  getKitchenChecklist,
  getDeliverySchedule,
  getStockAlerts,
} from '../lib/purchaseListEngine';
import { getOrders } from '../lib/insightsEngine';
import { getTopIngredients, getTopDishes, suggestRecipes } from '../lib/wishlistEngine';

const CATEGORY_LABELS = {
  proteina:    { label: 'Proteína',     icon: '🍗', color: 'tomate' },
  carboidrato: { label: 'Carboidrato',  icon: '🌾', color: 'mostard' },
  vegetal:     { label: 'Vegetais',     icon: '🥦', color: 'verde' },
  fruta:       { label: 'Frutas',       icon: '🍌', color: 'mostard' },
  laticinio:   { label: 'Laticínios',   icon: '🥛', color: 'mostard' },
  molho:       { label: 'Molhos/Caldos',icon: '🥄', color: 'mostard' },
  tempero:     { label: 'Temperos',     icon: '🌿', color: 'verde' },
  embalagem:   { label: 'Embalagens',   icon: '📦', color: 'ink' },
};

function fmtBRL(v) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}

function fmtQty(qty, unit) {
  if (unit === 'kg') {
    if (qty >= 1) return qty.toFixed(2).replace('.', ',') + ' kg';
    return (qty * 1000).toFixed(0) + ' g';
  }
  if (unit === 'l') {
    if (qty >= 1) return qty.toFixed(2).replace('.', ',') + ' L';
    return (qty * 1000).toFixed(0) + ' mL';
  }
  if (unit === 'un') return Math.ceil(qty) + ' un';
  return qty + ' ' + unit;
}

export default function OperationsPanel() {
  const horizons = getHorizons();
  const [horizon, setHorizon] = useState('amanha');
  const [tick, setTick] = useState(0);
  const [checked, setChecked] = useState({}); // checklist state

  const list = useMemo(() => getPurchaseList(horizon), [horizon, tick]);
  const kitchen = useMemo(() => getKitchenChecklist(), [tick]);
  const delivery = useMemo(() => getDeliverySchedule(horizon === 'amanha' ? 'esta_semana' : horizon), [horizon, tick]);
  const stock = useMemo(() => getStockAlerts(), [tick]);

  function refresh() {
    getOrders({ refresh: true });
    setTick(t => t + 1);
  }

  function toggle(id) {
    setChecked(prev => ({ ...prev, [id]: !prev[id] }));
  }

  function exportToText() {
    const lines = [];
    lines.push(`LISTA DE COMPRAS — ${list.horizonLabel.toUpperCase()}`);
    lines.push(`${list.metrics.totalOrders} pedidos · ${fmtBRL(list.metrics.totalRevenue)} receita estimada`);
    lines.push('');
    Object.entries(list.byCategory).forEach(([cat, items]) => {
      const meta = CATEGORY_LABELS[cat] || { label: cat, icon: '•' };
      lines.push(`${meta.icon} ${meta.label.toUpperCase()}`);
      items.forEach(it => {
        lines.push(`  [ ] ${it.name.padEnd(30)} ${fmtQty(it.qty, it.unit).padStart(10)}  · ${it.supplier}`);
      });
      lines.push('');
    });
    const txt = lines.join('\n');
    const blob = new Blob([txt], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lista-compras-${horizon}-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function printList() {
    if (typeof window !== 'undefined') window.print();
  }

  return (
    <div className="rl-ops-panel">
      {/* Header */}
      <div className="rl-ops-header">
        <div>
          <div className="rl-ops-eyebrow">Operação &amp; Rotina · próximos dias</div>
          <h1 className="rl-ops-title">A semana operacional</h1>
          <p className="rl-ops-sub">
            Lista de compras, checklist de cozinha e agenda de entregas — tudo otimizado pelos pedidos confirmados e padrão histórico.
          </p>
        </div>
        <div className="rl-ops-actions">
          <button className="rl-ops-refresh" onClick={refresh}>↻ Atualizar</button>
        </div>
      </div>

      {/* Horizonte switcher */}
      <div className="rl-horizon-switch">
        {Object.entries(horizons).map(([k, cfg]) => (
          <button
            key={k}
            onClick={() => setHorizon(k)}
            className={'rl-horizon-btn' + (horizon === k ? ' rl-horizon-btn--active' : '')}
          >
            <div className="rl-horizon-btn-label">{cfg.label}</div>
            <div className="rl-horizon-btn-range">
              {cfg.start === cfg.end ? `+${cfg.start} dia` : `+${cfg.start} a +${cfg.end} dias`}
            </div>
          </button>
        ))}
      </div>

      {/* KPIs do horizonte */}
      <div className="rl-kpi-grid">
        <div className="rl-kpi">
          <div className="rl-kpi-label">Pedidos previstos</div>
          <div className="rl-kpi-value">{list.metrics.totalOrders}</div>
          <div className="rl-kpi-sub">{list.horizonLabel.toLowerCase()}</div>
        </div>
        <div className="rl-kpi">
          <div className="rl-kpi-label">Receita estimada</div>
          <div className="rl-kpi-value">{fmtBRL(list.metrics.totalRevenue)}</div>
          <div className="rl-kpi-sub">apenas pedidos confirmados</div>
        </div>
        <div className="rl-kpi">
          <div className="rl-kpi-label">Itens da compra</div>
          <div className="rl-kpi-value">{list.metrics.totalItems}</div>
          <div className="rl-kpi-sub">SKUs distintos a comprar</div>
        </div>
        <div className="rl-kpi">
          <div className="rl-kpi-label">Fornecedores</div>
          <div className="rl-kpi-value">{Object.keys(list.bySupplier).length}</div>
          <div className="rl-kpi-sub">contatos a acionar</div>
        </div>
      </div>

      {/* Léve sugere ação */}
      <section className="rl-card rl-card--verde">
        <div className="rl-card-head">
          <span className="rl-leve-avatar">🌱</span>
          <div>
            <div className="rl-card-eyebrow">Léve sugere · operação de {list.horizonLabel.toLowerCase()}</div>
            <div className="rl-card-title">
              {list.metrics.totalOrders === 0
                ? 'Tá tranquilo, nenhum pedido confirmado por aqui ainda.'
                : `Manda ver! São ${list.metrics.totalOrders} pedidos pra preparar. Tô danado.`}
            </div>
          </div>
        </div>
        {list.metrics.totalOrders > 0 && (
          <ul className="rl-quick-actions">
            <li>📞 Acionar <strong>{Object.keys(list.bySupplier).join(', ')}</strong> hoje até 16h</li>
            <li>🍳 Mise en place {horizon === 'amanha' ? 'amanhã' : 'na segunda'} às 06h</li>
            <li>🛵 Saída de entrega às 11h — rota Recife sul</li>
            <li>📦 Conferir embalagens (estoque atual abaixo do recomendado)</li>
          </ul>
        )}
      </section>

      {/* Lista de compras por categoria */}
      <section className="rl-card">
        <div className="rl-card-head">
          <div>
            <div className="rl-card-eyebrow">Lista de compras · {list.horizonLabel}</div>
            <div className="rl-card-title">Compra agrupada por categoria</div>
          </div>
          <div className="rl-card-actions">
            <button className="rl-btn rl-btn--ghost rl-btn--small" onClick={exportToText}>⇩ Exportar .txt</button>
            <button className="rl-btn rl-btn--ghost rl-btn--small" onClick={printList}>🖨 Imprimir</button>
          </div>
        </div>

        {list.metrics.totalOrders === 0 && (
          <div className="rl-empty rl-empty--big">
            <div style={{ fontSize: 40, marginBottom: 8 }}>🤷</div>
            Nenhum pedido confirmado pra {list.horizonLabel.toLowerCase()}. Léve segue de olho.
          </div>
        )}

        {Object.entries(list.byCategory).map(([cat, items]) => {
          const meta = CATEGORY_LABELS[cat] || { label: cat, icon: '•', color: 'ink' };
          return (
            <div key={cat} className="rl-cat-block">
              <div className={'rl-cat-head rl-cat-head--' + meta.color}>
                <span className="rl-cat-icon">{meta.icon}</span>
                <span className="rl-cat-name">{meta.label}</span>
                <span className="rl-cat-count">{items.length} itens</span>
              </div>
              <table className="rl-buy-table">
                <thead>
                  <tr>
                    <th style={{ width: 28 }}></th>
                    <th>Ingrediente</th>
                    <th style={{ textAlign: 'right' }}>Quantidade</th>
                    <th>Fornecedor</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => {
                    const id = cat + '|' + it.name;
                    const isChecked = !!checked[id];
                    return (
                      <tr key={id} className={isChecked ? 'rl-buy-row rl-buy-row--checked' : 'rl-buy-row'}>
                        <td>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggle(id)}
                            aria-label={'Marcar ' + it.name + ' como comprado'}
                          />
                        </td>
                        <td className="rl-buy-name">{it.name}</td>
                        <td className="rl-buy-qty">{fmtQty(it.qty, it.unit)}</td>
                        <td className="rl-buy-supplier">{it.supplier}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
      </section>

      {/* Grid: checklist cozinha + agenda entregas */}
      <div className="rl-grid-2">
        <section className="rl-card">
          <div className="rl-card-head">
            <div>
              <div className="rl-card-eyebrow">Cozinha · checklist do dia</div>
              <div className="rl-card-title">Rotina operacional</div>
            </div>
          </div>
          <div className="rl-list">
            {kitchen.map(k => {
              const id = 'kitchen-' + k.id;
              const isChecked = !!checked[id];
              return (
                <label key={k.id} className={'rl-task' + (isChecked ? ' rl-task--done' : '')}>
                  <input type="checkbox" checked={isChecked} onChange={() => toggle(id)} />
                  <div className="rl-task-body">
                    <div className="rl-task-head">
                      <span className="rl-task-time">{k.time}</span>
                      <span className="rl-task-title">{k.title}</span>
                      <span className={'rl-task-tag rl-task-tag--' + k.priority}>{k.priority}</span>
                    </div>
                    <div className="rl-task-desc">{k.desc}</div>
                    <div className="rl-task-meta">⏱ {k.duration}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </section>

        <section className="rl-card">
          <div className="rl-card-head">
            <div>
              <div className="rl-card-eyebrow">Agenda · próximas entregas</div>
              <div className="rl-card-title">{delivery.length > 0 ? delivery.length + ' dias com entrega' : 'Nada agendado'}</div>
            </div>
          </div>
          <div className="rl-delivery-list">
            {delivery.length === 0 && <div className="rl-empty">Sem entregas no horizonte.</div>}
            {delivery.map((d, i) => (
              <div key={i} className="rl-delivery-row">
                <div className="rl-delivery-date">
                  <div className="rl-delivery-date-day">{d.date.getDate()}</div>
                  <div className="rl-delivery-date-month">{d.date.toLocaleDateString('pt-BR', { month: 'short' })}</div>
                </div>
                <div className="rl-delivery-body">
                  <div className="rl-delivery-title">
                    {d.date.toLocaleDateString('pt-BR', { weekday: 'long' })} · {d.total} marmitas
                  </div>
                  <div className="rl-delivery-meta">{fmtBRL(d.revenue)} · saída 11h</div>
                  <div className="rl-delivery-bairros">
                    {[...new Set(d.orders.slice(0, 6).map(o => o.customerName))].join(' · ')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Alertas de estoque */}
      <section className="rl-card">
        <div className="rl-card-head">
          <div>
            <div className="rl-card-eyebrow">Estoque · alertas</div>
            <div className="rl-card-title">Itens que precisam atenção</div>
          </div>
        </div>
        <div className="rl-stock-grid">
          {stock.map(s => (
            <div key={s.item} className={'rl-stock rl-stock--' + s.status}>
              <div className="rl-stock-head">
                <span className="rl-stock-name">{s.item}</span>
                <span className={'rl-stock-status rl-stock-status--' + s.status}>{s.status}</span>
              </div>
              <div className="rl-stock-stats">
                <span>atual: <strong>{s.stock}{s.unit ? ' ' + s.unit : ' un'}</strong></span>
                <span>ideal: {s.recommended}{s.unit ? ' ' + s.unit : ' un'}</span>
              </div>
              <div className="rl-stock-supplier">{s.supplier}</div>
            </div>
          ))}
        </div>
      </section>

      {/* === DESEJOS DOS CLIENTES === */}
      <WishlistAndRecipes />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bloco de Desejos + Receitas sugeridas (item 3 do feedback)
// ─────────────────────────────────────────────────────────────────────────────
function WishlistAndRecipes() {
  const topIng = useMemo(() => getTopIngredients({ limit: 8 }), []);
  const topDishes = useMemo(() => getTopDishes({ limit: 5 }), []);
  const recipes = useMemo(() => suggestRecipes({ limit: 4 }), []);

  return (
    <>
      <section className="rl-card rl-card--mostard">
        <div className="rl-card-head">
          <span className="rl-leve-avatar">🌱</span>
          <div>
            <div className="rl-card-eyebrow">Lista de desejos · assinantes &amp; pacotes mensais</div>
            <div className="rl-card-title">O que os clientes mais pedem pra ver no cardápio</div>
          </div>
        </div>

        <div className="rl-wish-grid">
          <div>
            <div className="caps-label">🥬 Top ingredientes desejados</div>
            <div className="rl-wish-list">
              {topIng.map((ing, i) => (
                <div key={ing.name} className="rl-wish-item">
                  <div className="rl-wish-rank">{i + 1}</div>
                  <div className="rl-wish-body">
                    <div className="rl-wish-name">{ing.name}</div>
                    <div className="rl-wish-meta">
                      {ing.count}× citado · {ing.uniqueCustomers} clientes
                      {ing.highPriorityShare > 0.5 && <span className="rl-tag" style={{ marginLeft: 6 }}>🔥 alta prioridade</span>}
                    </div>
                    {ing.nutrition && (
                      <div className="rl-wish-nutrition">
                        {ing.nutrition.kcal} kcal · {ing.nutrition.protein}g prot · {ing.nutrition.fiber}g fibra
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="caps-label">🍽 Pratos pedidos por nome</div>
            <div className="rl-wish-list">
              {topDishes.length === 0 && <div className="rl-empty">Sem pratos pedidos diretamente ainda.</div>}
              {topDishes.map((d, i) => (
                <div key={d.name} className="rl-wish-item">
                  <div className="rl-wish-rank rl-wish-rank--mostard">{i + 1}</div>
                  <div className="rl-wish-body">
                    <div className="rl-wish-name">{d.name}</div>
                    <div className="rl-wish-meta">{d.count}× · {d.uniqueCustomers} clientes</div>
                    {d.notes && d.notes.length > 0 && (
                      <div className="rl-wish-note">"{d.notes[0]}"</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Receitas sugeridas */}
      <section className="rl-card">
        <div className="rl-card-head">
          <div>
            <div className="rl-card-eyebrow">Receitas sugeridas pela combinação dos desejos</div>
            <div className="rl-card-title">🍳 O que vale levar para a chef testar</div>
          </div>
        </div>
        <div className="rl-recipes-grid">
          {recipes.map(r => (
            <div key={r.name} className="rl-recipe">
              <div className="rl-recipe-head">
                <div className="rl-recipe-name">{r.name}</div>
                <div className="rl-recipe-score">
                  {Array.from({ length: r.healthScore }, () => '●').join('')}
                  <span style={{ opacity: 0.3 }}>{Array.from({ length: 10 - r.healthScore }, () => '●').join('')}</span>
                </div>
              </div>
              <div className="rl-recipe-desc">{r.description}</div>
              <div className="rl-recipe-macros">
                <span>{r.profile.kcal} kcal</span>
                <span>{r.profile.protein}g prot</span>
                <span>{r.profile.carb}g carb</span>
                <span>{r.profile.fat}g gord</span>
              </div>
              <div className="rl-rag-tags">
                {r.tags.map(t => <span key={t} className="rl-tag">#{t}</span>)}
              </div>
              <div className="rl-recipe-base">
                <strong>Base:</strong> {r.base.join(' + ')}
                <br/>
                <strong>Realça com:</strong> {r.boost.join(', ')}
              </div>
            </div>
          ))}
          {recipes.length === 0 && (
            <div className="rl-empty rl-empty--big">
              Sem combinações ainda. Conforme mais clientes pedirem ingredientes específicos, a Léve sugere receitas que combinam.
            </div>
          )}
        </div>
      </section>
    </>
  );
}
